import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEye } from "react-icons/fi";
import moment from "moment";
import { getallWorkOrders } from "../../../Redux/actions/Operationsactions/getworkorders";
import StatusChip, { statusChipType } from "../../Common/StatusChip";
import ActionButton from "../../Common/ActionButton";
import SearchFilter from "../../Common/SearchFilter";
import SelectFilter from "../../Common/SelectFilter";
import ColumnVisibilityControl from "../../Common/ColumnVisibilityControl";
import { filterStoreProjects } from "../projectFilters";

/**
 * Contractor Bills Screen
 * Displays contractor running bills and work orders with progress billing.
 * Follows the project standard theme: AgGrid table, SearchFilter, SelectFilter, ActionButton, and KPI summary.
 */
const ContractorBills = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const gridRef = useRef(null);

  const rolesData = useSelector((state) => state.GetAllRole);
  const result = useSelector((state) => state.getAllWorkOrders);
  const projects = filterStoreProjects(rolesData?.data?.Projects);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    dispatch(getallWorkOrders(selectedProjectId || undefined));
  }, [dispatch, selectedProjectId]);

  // Filter rows: only work orders with bills, matching optional status filter
  const rows = useMemo(() => {
    return (result?.data || []).filter((workOrder) => {
      if (Number(workOrder.BillsCount || 0) === 0) {
        return false;
      }
      if (statusFilter && workOrder.Status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [result?.data, statusFilter]);

  // Column Definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Created on",
        field: "CreatedAt",
        flex: 1.1,
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("DD-MMM-YYYY") : "—",
      },
      {
        headerName: "WO Number",
        field: "WorkOrderNumber",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (params) => (
          <span
            style={{
              fontWeight: 600,
              color: "var(--color-primary, #0b6462)",
              cursor: "pointer",
            }}
            onClick={() =>
              history.push(
                `/works/work-orders/details/${params.data?.WorkOrderId || params.data?.Id}`,
              )
            }
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Contractor",
        field: "ContractorName",
        flex: 1.6,
        minWidth: 170,
      },
      {
        headerName: "Project",
        field: "ProjectName",
        flex: 1.4,
        minWidth: 150,
      },
      {
        headerName: "Bills Count",
        field: "BillsCount",
        width: 110,
        minWidth: 100,
        cellStyle: { textAlign: "center" },
        cellRenderer: (params) => (
          <span
            style={{
              backgroundColor: "#e7f4f4",
              color: "#0b6462",
              padding: "2px 10px",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "12px",
            }}
          >
            {params.value || 0}
          </span>
        ),
      },
      {
        headerName: "Contract Value",
        field: "TotalContractValue",
        flex: 1.3,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? `Rs. ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Pending Value",
        field: "TotalPendingValue",
        flex: 1.3,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? `Rs. ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Status",
        field: "Status",
        width: 130,
        cellRenderer: (params) => {
          const val = params.value || "N/A";
          return <StatusChip status={val} type={statusChipType(val)} />;
        },
      },
      {
        headerName: "Action",
        field: "action",
        width: 120,
        pinned: "right",
        sortable: false,
        filter: false,
        headerComponent: () => (
          <div className="d-flex justify-content-center w-100">
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/works/contractor-bills/add")}
              variant="save"
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ gap: "10px", height: "100%" }}
          >
            <FiEye
              title="View Contractor Bills"
              size="1.15em"
              style={{ cursor: "pointer", color: "#0b6462" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(
                  `/works/work-orders/details/${params.data?.WorkOrderId || params.data?.Id}`,
                );
              }}
            />
          </div>
        ),
      },
    ],
    [history],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "—" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <div className="pt-2 app_font">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Contractor Bills"
        >
          Contractor Bills
        </div>
        {/* Search Filter */}
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by WO Number, Contractor, Project..."
        />

        {/* Project Filter */}
        <SelectFilter
          id="projectFilter"
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          label="Project"
          defaultOptionLabel="All Projects"
          maxWidth="220px"
          options={projects.map((p) => ({
            value: p.ProjectId,
            label: p.ProjectName,
          }))}
        />

        {/* Status Filter */}
        <SelectFilter
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          label="Status"
          defaultOptionLabel="All Statuses"
          maxWidth="180px"
          options={[
            { value: "Approved", label: "Approved" },
            { value: "Pending", label: "Pending" },
            { value: "In Progress", label: "In Progress" },
            { value: "Completed", label: "Completed" },
          ]}
        />

        {/* Column Visibility Control */}
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* ── Table Grid ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height2 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(row) => {
                if (row.data) {
                  history.push(`/works/work-orders/details/${row.data.WorkOrderId || row.data.Id}`);
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorBills;
